import React from 'react'
import { Card, CardContent, CardHeader, Label, Switch } from '@/shared/ui'
import { useToast } from '@/shared/ui/Toast'
import {
  defaultSignalsOverlayLevels,
  getSignalsOverlayLevels,
  setSignalsOverlayLevel,
  type SignalsOverlayLevelKey,
  type SignalsOverlayLevels,
} from '../services/signalsOverlayLevelsSettingsService'

type Row = { key: SignalsOverlayLevelKey; title: string; description: string }

const ROWS: Row[] = [
  {
    key: 'entryInitial',
    title: 'Zona técnica inicial (entrada inicial)',
    description: 'Linha da entrada mínima (ou “Entrada” quando não há range).',
  },
  {
    key: 'entryMid',
    title: 'Zona de equilíbrio (preço médio)',
    description: 'Linha do preço médio da zona de entrada (entry_mid).',
  },
  {
    key: 'entryFinal',
    title: 'Zona técnica final (entrada final)',
    description: 'Linha da entrada máxima (quando há range).',
  },
  {
    key: 'stop',
    title: 'Invalidação (stop)',
    description: 'Linha de invalidação (stop efetivo).',
  },
  {
    key: 'targets',
    title: 'Projeções (alvos)',
    description: 'Linhas de Projeção 1/2/3 (TP1/TP2/TP3).',
  },
]

export default function SignalsOverlayLevelsSettings() {
  const { add: toast } = useToast()
  const [loading, setLoading] = React.useState(true)
  const [saving, setSaving] = React.useState(false)
  const [levels, setLevels] = React.useState<SignalsOverlayLevels>(defaultSignalsOverlayLevels())

  React.useEffect(() => {
    let mounted = true
    ;(async () => {
      try {
        const cur = await getSignalsOverlayLevels()
        if (!mounted) return
        setLevels(cur)
      } catch (e: any) {
        toast({
          title: 'Dados Técnicos (overlay)',
          description: e?.message || 'Falha ao carregar configurações',
          variant: 'error',
        })
      } finally {
        if (mounted) setLoading(false)
      }
    })()
    return () => {
      mounted = false
    }
  }, [toast])

  async function toggle(key: SignalsOverlayLevelKey, next: boolean) {
    if (saving) return
    setSaving(true)
    try {
      await setSignalsOverlayLevel(key, next)
      setLevels((prev) => ({ ...prev, [key]: next }))
      toast({
        title: 'Dados Técnicos (overlay)',
        description: `${ROWS.find((r) => r.key === key)?.title || key}: ${next ? 'ativado' : 'desativado'}.`,
        variant: 'success',
        soft: true,
      })
    } catch (e: any) {
      toast({
        title: 'Dados Técnicos (overlay)',
        description: e?.message || 'Falha ao salvar',
        variant: 'error',
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card>
      <CardHeader className="text-base font-semibold">Dados Técnicos (overlay)</CardHeader>
      <CardContent className="space-y-2">
        <p className="text-xs text-foreground/60">
          Controla quais linhas do overlay atual de Sinais aparecem no gráfico (para usuários com acesso ao módulo Sinais).
        </p>

        <div className="mt-2 space-y-2">
          {ROWS.map((row) => (
            <div
              key={row.key}
              className="flex items-center justify-between gap-4 rounded-md border border-border/60 px-3 py-2"
            >
              <div className="min-w-0">
                <div className="text-sm font-medium">{row.title}</div>
                <div className="text-xs text-foreground/60">{row.description}</div>
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-xs">Desativado</Label>
                <Switch
                  checked={!!(levels as any)[row.key]}
                  disabled={loading || saving}
                  onChange={(e) => toggle(row.key, (e.target as HTMLInputElement).checked)}
                />
                <Label className="text-xs">Ativado</Label>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

